import { Box, IconButton } from '@chakra-ui/react';
import type {
  CartRecommendationDisplayContext,
  CartRecommendationItem,
} from '@diamond/shared/types';
import {
  LabelPromoList,
  PromoPriceIntegration,
  Text,
} from '@diamond/shared/ui';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import {
  hasCountVouchers,
  processPromos,
  useVoucherDisplayEnabled,
} from '@diamond/sol-buyer/utils';
import { AddOutlined } from '@mui/icons-material';
import React from 'react';

import { Image } from '../../common/image';
import { VoucherAvailableBadge } from '../../voucher/VoucherAvailableBadge';

type CardStyle = {
  width: string;
  imageSize: string;
  imagePadding: string;
  badgeSize: 'sm' | 'md';
  titleFontSize: string;
  titleFontWeight: string;
  titleLineHeight: string;
  priceFontSize: string;
  priceFontWeight: string;
  priceLineHeight: string;
  unitFontSize: string;
  unitLineHeight: string;
  textColor: string;
};

const CARD_STYLE_BY_CONTEXT: Record<
  CartRecommendationDisplayContext,
  CardStyle
> = {
  add_to_cart_success_popup: {
    width: '120px',
    imageSize: '120px',
    imagePadding: '6px',
    badgeSize: 'sm',
    titleFontSize: '12px',
    titleFontWeight: '400',
    titleLineHeight: '15px',
    priceFontSize: '16px',
    priceFontWeight: '600',
    priceLineHeight: '19px',
    unitFontSize: '10px',
    unitLineHeight: '12px',
    textColor: '#373F41',
  },
  cart_page: {
    width: '144px',
    imageSize: '144px',
    imagePadding: '7px',
    badgeSize: 'sm',
    titleFontSize: '12px',
    titleFontWeight: '400',
    titleLineHeight: '15px',
    priceFontSize: '16px',
    priceFontWeight: '600',
    priceLineHeight: '19px',
    unitFontSize: '10px',
    unitLineHeight: '12px',
    textColor: '#373F41',
  },
};

function RecommendationBadges({
  processedPromo,
  showPromoLabels,
  showVoucherBadge,
  voucherSize,
}: {
  processedPromo: ReturnType<typeof processPromos>;
  showPromoLabels: boolean;
  showVoucherBadge: boolean;
  voucherSize: 'sm' | 'md';
}) {
  if (!showPromoLabels && !showVoucherBadge) return null;

  return (
    <Box
      display="flex"
      gap="1"
      alignItems="flex-start"
      flexWrap="wrap"
      overflow="hidden"
      mb="1"
    >
      {showPromoLabels && <LabelPromoList processedPromo={processedPromo} />}
      {showVoucherBadge && <VoucherAvailableBadge size={voucherSize} />}
    </Box>
  );
}

export interface CartRecommendationCardProps {
  item: CartRecommendationItem;
  onAdd: (item: CartRecommendationItem) => void;
  isAdding?: boolean;
  displayContext: CartRecommendationDisplayContext;
  onSelect?: (item: CartRecommendationItem) => void;
}

export function CartRecommendationCard({
  item,
  onAdd,
  isAdding = false,
  displayContext,
  onSelect,
}: CartRecommendationCardProps) {
  const { product } = item;
  const isVoucherDisplayEnabled = useVoucherDisplayEnabled();
  const cardStyle = CARD_STYLE_BY_CONTEXT[displayContext];

  const processedPromo = processPromos(product);
  const { hasDiscount, hasBundledDiscount, priceAfterDiscount } =
    processedPromo;

  const showVoucherBadge = isVoucherDisplayEnabled && hasCountVouchers(product);
  const showPromoLabels = hasDiscount || hasBundledDiscount;

  const unitPrice = product.prices?.[0]?.price ?? 0;
  const displayPrice = hasDiscount ? priceAfterDiscount : unitPrice;
  const isPriceZero = displayPrice <= 0;

  const handleAdd: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    onAdd(item);
  };

  return (
    <Box
      w={cardStyle.width}
      maxW={cardStyle.width}
      h="100%"
      bg="transparent"
      overflow="hidden"
      display="flex"
      flexDir="column"
      cursor={onSelect ? 'pointer' : 'default'}
      data-testid="cart-recommendation-card"
    >
      <Box
        className="cart-recommendation-surface"
        onClick={() => onSelect?.(item)}
        flex="1"
        display="flex"
        flexDir="column"
        transition="transform 0.18s ease"
        _hover={{
          transform: onSelect ? 'translateY(-2px)' : undefined,
        }}
        sx={{
          '&:hover .cart-recommendation-image': {
            borderColor: '#E4E7F8',
            boxShadow: '0 4px 12px rgba(0, 0, 163, 0.12)',
          },
          '&:hover .cart-recommendation-add': {
            backgroundColor: '#006ED8',
            boxShadow: '0 4px 10px rgba(0, 0, 163, 0.32)',
          },
        }}
      >
        <Box
          className="cart-recommendation-image"
          position="relative"
          h={cardStyle.imageSize}
          w={cardStyle.imageSize}
          bg="white"
          borderRadius="4px"
          overflow="hidden"
          mb="2"
          border="1px solid"
          borderColor="transparent"
          transition="border-color 0.18s ease, box-shadow 0.18s ease"
        >
          <Image
            src={product.product_image_thumbnails?.[0]}
            alt={product.title}
            fill
            sizes={cardStyle.imageSize}
            style={{
              objectPosition: 'center',
              objectFit: 'contain',
              padding: cardStyle.imagePadding,
            }}
          />

          <IconButton
            className="cart-recommendation-add"
            aria-label="Tambah ke keranjang"
            icon={<AddOutlined fontSize="small" />}
            size="sm"
            position="absolute"
            bottom="2"
            right="2"
            bgColor="#0000A3"
            color="white"
            borderRadius="md"
            boxShadow="0 2px 6px rgba(0, 0, 163, 0.22)"
            transition="background-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease"
            _hover={{
              bgColor: '#006ED8',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 10px rgba(0, 0, 163, 0.32)',
            }}
            _active={{
              bgColor: '#000066',
              transform: 'translateY(0)',
              boxShadow: '0 1px 4px rgba(0, 0, 163, 0.24)',
            }}
            _focusVisible={{
              boxShadow:
                '0 0 0 3px rgba(0, 0, 163, 0.24), 0 4px 10px rgba(0, 0, 163, 0.32)',
            }}
            isLoading={isAdding}
            onClick={handleAdd}
            data-testid="cart-recommendation-add"
          />
        </Box>

        <Box px="0" pb="0" display="flex" flexDir="column" flex="1">
          <RecommendationBadges
            processedPromo={processedPromo}
            showPromoLabels={showPromoLabels}
            showVoucherBadge={showVoucherBadge}
            voucherSize={cardStyle.badgeSize}
          />

          <Text
            fontSize={cardStyle.titleFontSize}
            fontWeight={cardStyle.titleFontWeight}
            noOfLines={2}
            lineHeight={cardStyle.titleLineHeight}
            minH="30px"
            mb="1"
            color={cardStyle.textColor}
          >
            {product.title}
          </Text>

          <Box mt="auto">
            {showPromoLabels && (
              <Box mb="0.5">
                <PromoPriceIntegration
                  data={product}
                  isBundled={hasBundledDiscount && !hasDiscount}
                />
              </Box>
            )}

            <Box>
              <Text
                as="span"
                fontWeight={cardStyle.priceFontWeight}
                fontSize={cardStyle.priceFontSize}
                lineHeight={cardStyle.priceLineHeight}
                color={cardStyle.textColor}
              >
                {isPriceZero ? '-' : buyerCurrencyFormatter(displayPrice)}
              </Text>
              {!isPriceZero && product.sales_unit && (
                <Text
                  as="span"
                  color={cardStyle.textColor}
                  fontSize={cardStyle.unitFontSize}
                  lineHeight={cardStyle.unitLineHeight}
                >
                  {' '}
                  /{product.sales_unit}
                </Text>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
